import React, { useMemo, useState } from 'react';

import type { PopoverProps } from '@mui/material';
import { IconButton, List, ListItemButton, Popover, Typography } from '@mui/material';
import { matchSorter } from 'match-sorter';
import { uniqBy } from 'ramda';
import {
  AlineCheckbox,
  AlineChip,
  AlineTooltip,
  ConfirmationDialog,
  SearchInput,
  Spacer,
} from 'src/components';
import { useSetFolderSyncJobLabels } from 'src/components/admin/integrations/useFolderSyncJobs';
import { SkinnyPlusIcon } from 'src/components/icons/SkinnyPlusIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  useDeleteLabel,
  useFindOrCreateLabel,
  useGetCheckWorkflowAuthorization,
  useGetIsAuthorizedToEditStorable,
  useGetUserLabels,
  useSetDocumentLabels,
  useSetGroupPermissions,
  useSetWorkflowLabels,
} from 'src/hooks';
import type { LabelSubset, UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import { makeStyles } from 'tss-react/mui';

import type { FolderSyncJobWithLabels } from './LabelList';

export const labelPopoverWidthPx = 400;
export const labelPopoverMaxHeightPx = 500;
export const smallCheckboxHeightPx = 24;
export const smallCheckboxWidthPx = 24;
const useStyles = makeStyles<{ hasLabels: boolean }>()((theme, { hasLabels }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  footer: {
    borderTop: hasLabels ? `1px solid ${theme.palette.divider}` : undefined,
    padding: theme.spacing(1),
  },
  ghostCheckbox: {
    alignItems: 'center',
    display: 'flex',
    height: smallCheckboxHeightPx,
    justifyContent: 'center',
    width: smallCheckboxWidthPx,
  },
  labelList: {
    padding: theme.spacing(1),
  },
  paper: {
    maxHeight: labelPopoverMaxHeightPx,
    width: 320,
  },
  searchContainer: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
}));

const useRowStyles = makeStyles<void, 'listItem'>()((theme, _params, classes) => ({
  hiddenCheckbox: {
    [`.${classes.listItem}:hover &, input:checked ~ *`]: {
      visibility: 'visible',
    },
    boxSizing: 'border-box',
    height: smallCheckboxHeightPx,
    visibility: 'hidden',
    width: smallCheckboxWidthPx,
  },
  listItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
  },
}));

export interface LabelEditorPopoverProps extends Required<
  Pick<PopoverProps, 'anchorEl' | 'anchorOrigin' | 'onClose' | 'open' | 'transformOrigin'>
> {
  style?: React.CSSProperties;
}
interface LabelEditorProps {
  onLabelToggle: (label: LabelSubset) => Promise<void>;
  popoverProps: LabelEditorPopoverProps;
  selectedLabels: LabelSubset[];
  disabledReason?: string;
}

export function LabelEditor({
  disabledReason = '',
  onLabelToggle,
  popoverProps,
  selectedLabels,
}: LabelEditorProps) {
  const { classes: rowClasses } = useRowStyles();
  const { deleteLabel } = useDeleteLabel();
  const { labels: allLabels } = useGetUserLabels();
  const { findOrCreateLabel } = useFindOrCreateLabel();
  const [searchText, setSearchText] = useState<string>('');
  const sortedLabels = useMemo(() => {
    return matchSorter(allLabels, searchText, {
      keys: ['labelText'] satisfies Array<keyof LabelSubset>,
    });
  }, [allLabels, searchText]);
  const selectedLabelIds = useMemo(
    () => new Set(selectedLabels.map(label => label.id)),
    [selectedLabels]
  );
  const isDuplicateLabel = useMemo(() => {
    return allLabels.some(label => label.labelText.toLowerCase() === searchText.toLowerCase());
  }, [allLabels, searchText]);
  const hasLabels = Boolean(sortedLabels.length);
  const { classes } = useStyles({ hasLabels });
  const isDisabled = Boolean(disabledReason);
  const [labelToDelete, setLabelToDelete] = useState<LabelSubset | null>(null);

  async function handleCreateLabel() {
    if (isDisabled || !searchText.trim() || isDuplicateLabel) return;

    const newLabel = await findOrCreateLabel(searchText);
    if (!newLabel) return;

    await onLabelToggle(newLabel);
  }

  return (
    <>
      <Popover
        classes={{ paper: classes.paper }}
        onClick={e => e.stopPropagation()}
        // Prevent MUI DataGrid from intercepting keyboard events inside the popover.
        // See https://github.com/mui/mui-x/issues/8970.
        onKeyDown={e => e.stopPropagation()}
        {...popoverProps}
      >
        <div className={classes.container}>
          <div className={classes.searchContainer}>
            <SearchInput
              minWidth="unset"
              placeholder="Search or add labels..."
              search={searchText}
              setSearch={setSearchText}
            />
          </div>
          {hasLabels && (
            <List className={classes.labelList}>
              {sortedLabels.map(label => (
                <LabelRow
                  key={label.id}
                  disabledReason={disabledReason}
                  isSelected={selectedLabelIds.has(label.id)}
                  label={label}
                  onDeleteClick={setLabelToDelete}
                  onLabelToggle={onLabelToggle}
                />
              ))}
            </List>
          )}
          {searchText && !isDuplicateLabel && (
            <div className={classes.footer}>
              <AlineTooltip arrow placement="top" title={disabledReason}>
                <div>
                  <ListItemButton
                    className={rowClasses.listItem}
                    disabled={isDisabled}
                    onClick={handleCreateLabel}
                  >
                    <div className={classes.ghostCheckbox}>
                      <SkinnyPlusIcon />
                    </div>
                    <Typography noWrap>{`Create "${searchText}" label`}</Typography>
                  </ListItemButton>
                </div>
              </AlineTooltip>
            </div>
          )}
        </div>
      </Popover>
      <ConfirmationDialog
        message={`Are you sure you want to delete the "${labelToDelete?.labelText ?? ''}" label?`}
        open={Boolean(labelToDelete)}
        title="Delete Label"
        onClickPrimary={async () => {
          if (labelToDelete) await deleteLabel(labelToDelete.id);
        }}
        onClose={() => setLabelToDelete(null)}
      />
    </>
  );
}

interface LabelRowProps {
  disabledReason: string;
  isSelected: boolean;
  label: LabelSubset;
  onDeleteClick: (label: LabelSubset) => void;
  onLabelToggle: (label: LabelSubset) => Promise<void>;
}

function LabelRow({
  disabledReason,
  isSelected,
  label,
  onDeleteClick,
  onLabelToggle,
}: LabelRowProps) {
  const { classes } = useRowStyles();
  const isDisabled = Boolean(disabledReason);
  const [mouseOver, setMouseOver] = useState(false);

  return (
    <AlineTooltip key={label.id} arrow placement="top" title={disabledReason}>
      <div>
        <ListItemButton
          className={classes.listItem}
          disabled={isDisabled}
          onClick={async e => {
            e.stopPropagation();
            await onLabelToggle(label);
          }}
          onMouseEnter={() => setMouseOver(true)}
          onMouseLeave={() => setMouseOver(false)}
        >
          <AlineCheckbox
            checked={isSelected}
            className={classes.hiddenCheckbox}
            disabled={isDisabled}
            size="small"
          />
          <AlineChip chipText={label.labelText} />
          <Spacer />
          <IconButton
            size="small"
            style={{ visibility: mouseOver ? 'visible' : 'hidden' }}
            onClick={async e => {
              e.stopPropagation();
              onDeleteClick(label);
            }}
          >
            <TrashcanIcon height={16} width={16} />
          </IconButton>
        </ListItemButton>
      </div>
    </AlineTooltip>
  );
}

export interface WorkflowWithLabels extends Pick<Apollo.Workflow, 'id' | '__typename'> {
  labels: LabelSubset[];
  owner: UserWithOrg;
  permissions: WorkflowPermissionSubset[];
}

export function WorkflowLabelEditor({
  popoverProps,
  workflow,
}: {
  popoverProps: LabelEditorPopoverProps;
  workflow: WorkflowWithLabels;
}) {
  const currentUser = useSelectCurrentUser();
  const { setWorkflowLabels } = useSetWorkflowLabels();
  const { checkWorkflowAuthorization } = useGetCheckWorkflowAuthorization();
  const { canEditWorkflow } = checkWorkflowAuthorization(workflow, currentUser);

  async function handleLabelToggle(label: LabelSubset) {
    const newLabelIds = new Set(workflow.labels.map(l => l.id));

    if (newLabelIds.has(label.id)) newLabelIds.delete(label.id);
    else newLabelIds.add(label.id);

    const newLabels = uniqBy(
      l => l.id,
      [...workflow.labels, label].filter(l => newLabelIds.has(l.id))
    );

    await setWorkflowLabels({
      labels: newLabels,
      workflowId: workflow.id,
    });
  }

  return (
    <LabelEditor
      disabledReason={!canEditWorkflow ? 'Only the workflow owner and editors can edit labels' : ''}
      popoverProps={popoverProps}
      selectedLabels={workflow.labels}
      onLabelToggle={handleLabelToggle}
    />
  );
}

export interface DocumentWithLabels extends Pick<
  Apollo.Document,
  'id' | 'requesterRole' | '__typename'
> {
  labels: LabelSubset[];
  owner: Pick<Apollo.User, 'id' | '__typename'>;
}

export function DocumentLabelEditor({
  document,
  documentLabels,
  popoverProps,
}: {
  document: DocumentWithLabels;
  documentLabels: LabelSubset[];
  popoverProps: LabelEditorPopoverProps;
}) {
  const { labels: allLabels } = useGetUserLabels();
  const { setDocumentLabels } = useSetDocumentLabels();
  const { getIsAuthorizedToEditStorable } = useGetIsAuthorizedToEditStorable();
  const { canEdit } = getIsAuthorizedToEditStorable(document);

  async function handleLabelToggle(label: LabelSubset) {
    const newLabelIds = new Set(documentLabels.map(l => l.id));

    if (newLabelIds.has(label.id)) newLabelIds.delete(label.id);
    else newLabelIds.add(label.id);

    const newLabels = uniqBy(
      l => l.id,
      [...allLabels, label].filter(l => newLabelIds.has(l.id))
    );

    await setDocumentLabels({
      documentId: document.id,
      labels: newLabels,
    });
  }

  return (
    <LabelEditor
      disabledReason={!canEdit ? 'Only the document owner and editors can edit labels' : ''}
      popoverProps={popoverProps}
      selectedLabels={documentLabels}
      onLabelToggle={handleLabelToggle}
    />
  );
}

export interface GroupWithLabels extends Pick<Apollo.Group, 'id' | '__typename'> {
  labels: LabelSubset[];
}

export function GroupLabelEditor({
  group,
  popoverProps,
}: {
  group: GroupWithLabels;
  popoverProps: LabelEditorPopoverProps;
}) {
  const currentUser = useSelectCurrentUser();
  const { setGroupPermissions } = useSetGroupPermissions();
  const isAdmin = currentUser?.role === Apollo.OrgRole.Admin;

  return (
    <LabelEditor
      disabledReason={!isAdmin ? 'Only admins can edit group labels' : ''}
      popoverProps={popoverProps}
      selectedLabels={group.labels}
      onLabelToggle={async label => {
        const isExistingLabel = group.labels.some(l => l.id === label.id);

        await setGroupPermissions(group, { label, type: isExistingLabel ? 'remove' : 'add' });
      }}
    />
  );
}

export function FolderSyncJobLabelEditor({
  folderSyncJob,
  popoverProps,
}: {
  folderSyncJob: FolderSyncJobWithLabels;
  popoverProps: LabelEditorPopoverProps;
}) {
  const { labels: allLabels } = useGetUserLabels();
  const { setFolderSyncJobLabels } = useSetFolderSyncJobLabels();

  async function handleLabelToggle(label: LabelSubset) {
    const newLabelIds = new Set(folderSyncJob.labels.map(l => l.id));

    if (newLabelIds.has(label.id)) newLabelIds.delete(label.id);
    else newLabelIds.add(label.id);

    await setFolderSyncJobLabels({
      folderSyncJobId: folderSyncJob.id,
      labels: uniqBy(
        l => l.id,
        [...allLabels, label].filter(l => newLabelIds.has(l.id))
      ),
    });
  }

  return (
    <LabelEditor
      popoverProps={popoverProps}
      selectedLabels={folderSyncJob.labels}
      onLabelToggle={handleLabelToggle}
    />
  );
}
